import type { AxiosResponse } from 'axios';
import { apiClient } from './client';

let refreshRequest: Promise<AxiosResponse<RefreshResponse>> | null = null;

function refreshSession(): Promise<AxiosResponse<RefreshResponse>> {
  refreshRequest ??= apiClient.post<RefreshResponse>('/auth/refresh', {}).finally(() => {
    refreshRequest = null;
  });
  return refreshRequest;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember_me: boolean;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  terms_accepted: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  username: string;
  role: 'user' | 'partner_admin' | 'demo_admin' | 'admin' | 'root_admin';
  is_active: boolean;
  is_verified: boolean;
  is_adult_verified: boolean;
  credits: number;
  membership_type: 'free' | 'basic' | 'premium' | 'vip';
  membership_expires_at?: string | null;
  created_at: string;
  full_name?: string | null;
  permissions: string[];
  doi_config: {
    required: boolean;
    delay_minutes: number;
    registration_timestamp: string | null;
  };
}

export interface AuthResponse {
  user: AuthUser;
}

export interface RefreshResponse {
  refreshed: true;
}

export type SessionStatusResponse = { authenticated: false; user: null } | { authenticated: true; user: AuthUser };

export function createLoginForm(data: LoginRequest): URLSearchParams {
  const formData = new URLSearchParams();
  formData.append('username', data.email);
  formData.append('password', data.password);
  formData.append('remember_me', String(data.remember_me));
  return formData;
}

export const authAPI = {
  login: (data: LoginRequest) => {
    return apiClient.post<AuthResponse>('/auth/login', createLoginForm(data), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  register: (data: RegisterRequest, params?: { referred_by?: string; referral_source?: string }) => {
    const query = new URLSearchParams();
    if (params?.referred_by) query.set('referred_by', params.referred_by);
    if (params?.referral_source) query.set('referral_source', params.referral_source);
    const qs = query.toString();
    return apiClient.post<AuthResponse>(`/auth/register${qs ? `?${qs}` : ''}`, data);
  },
  getSessionStatus: () => apiClient.get<SessionStatusResponse>('/auth/session'),
  getCurrentUser: () => apiClient.get<AuthUser>('/auth/me'),
  // The backend reads the HttpOnly refresh cookie. Frontend code must not try
  // to inspect or duplicate that credential.
  // Refresh tokens rotate on every successful call. All concurrent callers
  // must share one request so they cannot invalidate each other's cookie.
  refresh: refreshSession,
  // Logout via Backend, das Cookies löscht
  logout: () => apiClient.post('/auth/logout', {}),
  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) =>
    apiClient.post('/auth/reset-password', { token, new_password }),
  resendVerification: () => apiClient.post('/auth/resend-verification', {}),
};

// Keep the existing authService for backward compatibility
export const authService = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login', createLoginForm(data), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    return response.data;
  },

  async register(
    data: RegisterRequest,
    params?: { referred_by?: string; referral_source?: string }
  ): Promise<AuthResponse> {
    const query = new URLSearchParams();
    if (params?.referred_by) query.set('referred_by', params.referred_by);
    if (params?.referral_source) query.set('referral_source', params.referral_source);
    const qs = query.toString();
    const response = await apiClient.post<AuthResponse>(`/auth/register${qs ? `?${qs}` : ''}`, data);
    return response.data;
  },

  async getCurrentUser() {
    const response = await apiClient.get<AuthUser>('/auth/me');
    return response.data;
  },

  async getSessionStatus(): Promise<SessionStatusResponse> {
    const response = await apiClient.get<SessionStatusResponse>('/auth/session');
    return response.data;
  },

  async refresh(): Promise<RefreshResponse> {
    const response = await refreshSession();
    return response.data;
  },

  logout() {
    // Request an Backend – Cookies werden dort gelöscht
    void apiClient.post('/auth/logout', {}).finally(() => {
      window.location.href = '/auth';
    });
  },
};
